import React, { useState, useEffect } from 'react';
import {
  Input,
  Select,
  Button,
  ColumnLayout,
  SpaceBetween,
  Box,
  Checkbox,
  Pagination,
  TextContent,
} from '@cloudscape-design/components';
import { Location } from 'src/types/helios/Location';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import './LocationSelectionPanel.css';

interface LocationSelectionPanelProps {
  selectedLocations: Location[];
  locations: Location[];
  onChange: (locations: Location[]) => void;
}

const LocationSelectionPanel: React.FC<LocationSelectionPanelProps> = ({
  selectedLocations,
  onChange,
  locations,
}) => {
  const { closeView } = useSplitPanel();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [selected, setSelected] = useState<Location[]>(selectedLocations);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const regions = [
    'All Regions',
    ...locations
      .map((loc) => loc.region)
      .filter((region, index, self) => self.indexOf(region) === index)
      .sort((a, b) => a.localeCompare(b)),
  ];

  const countries = [
    'All Countries',
    ...locations
      .filter((loc) => selectedRegion === 'All Regions' || loc.region === selectedRegion)
      .map((loc) => loc.country)
      .filter((country, index, self) => self.indexOf(country) === index)
      .sort((a, b) => a.localeCompare(b)),
  ];

  const filteredLocations = locations
    .filter((location) => {
      // Split search term by comma, trim whitespace, and filter out empty strings
      const searchTerms = searchTerm
        .split(',')
        .map((term) => term.trim().toLowerCase())
        .filter((term) => term.length > 0);

      // If no search terms, match all (or just apply region/country filters)
      const matchesSearch =
        searchTerms.length === 0 ||
        searchTerms.some((term) => location.locationCode.toLowerCase().includes(term));

      const matchesRegion =
        selectedRegion === 'All Regions' || location.region === selectedRegion;
      const matchesCountry =
        selectedCountry === 'All Countries' || location.country === selectedCountry;
      return matchesSearch && matchesRegion && matchesCountry;
    })
    .sort((a, b) => a.locationCode.localeCompare(b.locationCode));

  const totalItems = filteredLocations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedLocations = filteredLocations.slice(startIndex, endIndex);

  const showingText =
    totalItems > 0
      ? `Showing ${startIndex + 1}-${endIndex} of ${totalItems}`
      : 'No locations found';

  const handleLocationToggle = (locationId: string) => {
    const location = locations.find((loc) => loc.locationCode === locationId);
    if (!location) return;

    const isSelected = selected.some((loc) => loc.locationCode === locationId);
    const newSelected = isSelected
      ? selected.filter((loc) => loc.locationCode !== locationId)
      : [...selected, location];
    setSelected(newSelected);
  };

  const handleSelectAll = (locations: Location[]) => {
    // Add filtered locations to existing selection, avoiding duplicates
    const newSelected = [...selected];
    locations.forEach((location) => {
      if (!newSelected.some((sel) => sel.locationCode === location.locationCode)) {
        newSelected.push(location);
      }
    });
    setSelected(newSelected);
  };

  const handleDeselectAll = () => {
    setSelected([]);
  };

  const handleDone = () => {
    onChange(selected);
    closeView();
  };

  const handleCancel = () => {
    setSelected(selectedLocations); // Reset to original selection
    closeView();
  };

  // Update local state when selectedLocations prop changes
  useEffect(() => {
    if (JSON.stringify(selectedLocations) !== JSON.stringify(selected)) {
      setSelected(selectedLocations);
    }
  }, [selectedLocations]);

  // Reset filters when locations prop changes (business unit change)
  useEffect(() => {
    setSearchTerm('');
    setSelectedRegion('All Regions');
    setSelectedCountry('All Countries');
    setCurrentPage(1);
  }, [locations]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRegion, selectedCountry]);

  // Reset country selection when region changes
  useEffect(() => {
    setSelectedCountry('All Countries');
  }, [selectedRegion]);

  return (
    <div className="location-selection-container">
      <SpaceBetween size="m">
        <ColumnLayout columns={1}>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.detail.value)}
            placeholder="Search locations (comma-separated for multiple)..."
          />
        </ColumnLayout>
        <ColumnLayout columns={2}>
          <Select
            selectedOption={{ label: selectedRegion, value: selectedRegion }}
            onChange={({ detail }) =>
              setSelectedRegion(detail.selectedOption.value || 'All Regions')
            }
            options={regions.map((region) => ({ label: region, value: region }))}
          />
          <Select
            selectedOption={{ label: selectedCountry, value: selectedCountry }}
            onChange={({ detail }) =>
              setSelectedCountry(detail.selectedOption.value || 'All Countries')
            }
            options={countries.map((country) => ({ label: country, value: country }))}
          />
        </ColumnLayout>

        <div className="location-selection-actions-container">
          <div className="location-selection-actions">
            <TextContent>
              <small>{showingText}</small>
            </TextContent>
            <SpaceBetween direction="horizontal" size="s">
              <Button onClick={() => handleSelectAll(filteredLocations)} variant="link">
                Select Filtered ({filteredLocations.length})
              </Button>
              <Button onClick={handleDeselectAll} variant="link">
                Deselect All ({selected.length})
              </Button>
            </SpaceBetween>
          </div>
        </div>
      </SpaceBetween>

      <div className="location-selection-content">
        <ColumnLayout columns={3}>
          {paginatedLocations.map((location: Location) => (
            <Checkbox
              key={location.locationCode}
              checked={selected.some((loc) => loc.locationCode === location.locationCode)}
              onChange={() => handleLocationToggle(location.locationCode)}
            >
              {location.locationCode}
            </Checkbox>
          ))}
        </ColumnLayout>
      </div>

      <SpaceBetween size="m">
        <div className="location-selection-pagination">
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
        </div>

        <div className="location-selection-footer">
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button variant="primary" onClick={handleDone}>
              Apply Selection ({selected.length})
            </Button>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </div>
  );
};

export default LocationSelectionPanel;
